import { fitReelText, REEL_TEXT_SLOTS } from "./lead-gen-reel-text";
import { leadGenAdLicenseWidth } from "./lead-gen-ad-measure";
import {
  reelTitleLayout,
  type ReelArtDirection,
} from "./lead-gen-reel-direction";
import { contrastRatio, parseHexColor, relativeLuminance } from "./color";

export const REEL_CONTRAST_MIN = 4.5;
// Headroom for raster rounding and video compression; not a compliance claim.
export const REEL_CONTRAST_TARGET = 5;
export const REEL_TITLE_LAYOUT = {
  x: 100,
  y: 520,
  width: 880,
  height: 430,
  entryOffset: 65,
} as const;
// Includes scale/pan and entry motion. Full-width feathered scrim, no text box.
export const REEL_TITLE_PROTECTION = {
  top: 470,
  bottom: 1080,
  feather: 260,
} as const;

export function blendReelColor(
  color: string,
  target: string,
  amount: number,
): string {
  const from = parseHexColor(color);
  const to = parseHexColor(target);
  if (!from || !to) throw new Error("Invalid Reel color");
  return `#${(["r", "g", "b"] as const)
    .map((key) =>
      Math.round(from[key] * (1 - amount) + to[key] * amount)
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;
}
export function reelContrast(a: string, b: string): number {
  const ratio = contrastRatio(a, b);
  if (ratio == null) throw new Error("Invalid Reel contrast colors");
  return ratio;
}
export function reelTextBackdrop(text: string): string {
  return (relativeLuminance(text) ?? 0) > 0.179 ? "#000000" : "#ffffff";
}
/** Keep the requested hue where possible; repair lightness only when unreadable. */
export function fitReelColor(
  color: string,
  against: string,
  minimum = REEL_CONTRAST_TARGET,
): string {
  if (reelContrast(color, against) >= minimum) return color;
  const end = reelTextBackdrop(against);
  for (let step = 1; step <= 100; step++) {
    const candidate = blendReelColor(color, end, step / 100);
    if (reelContrast(candidate, against) >= minimum) return candidate;
  }
  // Mid-gray can have <5 against both endpoints, but always supports >=4.5.
  if (reelContrast(end, against) >= REEL_CONTRAST_MIN) return end;
  throw new Error("Reel color pair cannot meet minimum contrast");
}
export function reelVisualLayoutBrief(story: {
  hook: string;
  bridge: string;
  artDirection?: ReelArtDirection;
}) {
  const layout = reelTitleLayout(story.artDirection);
  const slot = REEL_TEXT_SLOTS.hook;
  const width = Math.max(
    ...[story.hook, story.bridge].map((text) => {
      const fitted = fitReelText(
        text,
        slot.width,
        slot.maxLines,
        slot.maxSize,
        slot.minSize,
      );
      return (
        Math.max(
          ...fitted.lines.map((line) =>
            leadGenAdLicenseWidth(line, fitted.size),
          ),
        ) * 1.08
      );
    }),
  );
  const zone = {
    left: Math.max(80, Math.floor(540 - width / 2 - 96)),
    top: layout.y,
    right: Math.min(1000, Math.ceil(540 + width / 2 + 96)),
    bottom: layout.y + layout.height + layout.entryOffset,
  };
  return {
    canvas: { width: 1080, height: 1920 },
    titleLayer: layout,
    protectedBand: {
      top: layout.y,
      bottom: layout.y + layout.height + layout.entryOffset,
      feather: 380,
    },
    objectExclusionZone: {
      ...zone,
      normalized: {
        left: zone.left / 1080,
        top: zone.top / 1920,
        right: zone.right / 1080,
        bottom: zone.bottom / 1920,
      },
    },
    openingText: story.hook,
    bridgeText: story.bridge,
    typography:
      "Bold Outfit, 72–88px, at most two lines, alignment follows titleLayer.align; rendered separately",
    composition:
      "Reserve the entire objectExclusionZone, including breathing room around the title, as continuous quiet negative space. No laptops, cups, lamps, books, plants, furniture edges, window frames, strong shadows or other identifiable objects/contours may enter this zone, even if the letters would remain readable. Use quiet negative space as one part of a deliberate composition, not an entire empty-wall photograph. Place a strong focal anchor above/below or safely outside the exclusion zone: architectural depth, a dramatic foreground frame, sculptural perspective or controlled reflections. The center must stay calm while the full image earns attention. Keep natural tonal separation; never paint a rectangular white or black band into the photograph. Do not bake in copy. Allow room for subtle camera movement.",
  };
}
